import { Link } from "react-router-dom";
import {
    ArrowRight,
    ClipboardCheck,
    Users,
    Eye,
    CalendarClock,
    CheckCircle2,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import useReveal from "../hooks/useReveal";
import usePageMeta from "../hooks/usePageMeta";
import useFAQSchema from "../hooks/useFAQSchema";
import { FAQ_SERVICES } from "../data/faqs";

/* ──────────────────────────────────────────────────────────────────
 *  /services — Independent Roof & External-Building Assessment
 *  Section rhythm:
 *    1. Hero                      white
 *    2. Reporting Lead            white
 *    3. Four-Stage Pathway        white   (replaces 6-card grid)
 *    4. Report Preview            white
 *    5. Property Types (one line) white   (replaces 6-card industries grid)
 *    6. Final CTA                 navy
 * ────────────────────────────────────────────────────────────────── */

const EYEBROW =
    "font-display font-bold tracking-[0.26em] uppercase text-srm-gold text-[12px] lg:text-[13px]";
const GOLD_RULE = "block h-[3px] w-16 bg-srm-gold";

/* Four-stage independent pathway — matches Homepage and Guardianship terminology */
const STAGES = [
    {
        step: "01",
        icon: ClipboardCheck,
        title: "Assessment & Report",
        body:
            "Independent roof or external-building assessment, photographic evidence, risk priorities and committee-ready reporting.",
    },
    {
        step: "02",
        icon: Users,
        title: "Scope of Works & Tender",
        body:
            "Independent scope preparation, identification of suitable contractors, preliminary licence and insurance checks, controlled tendering and comparable submissions.",
    },
    {
        step: "03",
        icon: Eye,
        title: "Delivery Oversight",
        body:
            "Owner-side oversight, progress and milestone reviews, observed-quality reporting, completion evidence and approval recommendations.",
    },
    {
        step: "04",
        icon: CalendarClock,
        title: "Ongoing Guardianship & Monitoring",
        body:
            "Continuing asset records, annual or agreed monitoring, completed-work history, storm-event updates where engaged and long-term planning continuity.",
    },
];

const REPORT_SECTIONS = [
    "Executive Summary written for committee decision-making",
    "Assessment methodology and zones accessed",
    "Annotated roof zone mapping with condition status",
    "Defect register with date- and zone-referenced photography",
    "Prioritised recommendations across immediate, short-term and lifecycle works",
];

/* === 1. Hero ====================================================== */
function ServicesHero() {
    const ref = useReveal();
    return (
        <section ref={ref} data-testid="services-hero" className="bg-white">
            <div className="container-srm pt-12 pb-10 lg:pt-16 lg:pb-12">
                <div className="reveal max-w-[760px] mx-auto text-center">
                    <div className={EYEBROW}>Services</div>
                    <span aria-hidden className={`mx-auto mt-4 ${GOLD_RULE}`} />
                    <h1
                        data-testid="services-hero-headline"
                        className="mt-5 font-display font-bold text-[1.95rem] sm:text-[2.3rem] lg:text-[2.7rem] tracking-[-0.024em] leading-[1.1] text-srm-navy text-balance"
                    >
                        Independent roof and external-building assessment services.
                    </h1>
                    <p className="mt-6 text-[15.5px] lg:text-[17px] text-srm-body leading-[1.8] max-w-[620px] mx-auto">
                        Assessment, professionally documented reporting, procurement
                        support, delivery oversight and continuing monitoring — one
                        independent partner across the life of the roof.
                    </p>
                    <p
                        data-testid="services-hero-support"
                        className="mt-4 text-[14px] lg:text-[15px] text-srm-body leading-[1.75] max-w-[620px] mx-auto"
                    >
                        SRM provides independent roof condition assessments and
                        committee-ready reports for strata schemes, body corporates and
                        professionally managed buildings, including{" "}
                        <Link
                            to="/sunshine-coast-strata-roof-assessments"
                            data-testid="services-link-sunshine-coast"
                            className="text-srm-navy font-medium underline underline-offset-4 decoration-srm-gold/60 hover:text-srm-gold transition-colors"
                        >
                            strata roof assessments on the Sunshine Coast
                        </Link>
                        .
                    </p>
                </div>
            </div>
        </section>
    );
}

/* === Reporting Lead — See the standard behind SRM reporting ===== */
function ReportingLead() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="services-reporting-lead"
            className="bg-white border-t border-srm-line"
        >
            <div className="container-srm py-12 lg:py-16">
                <div className="max-w-4xl mx-auto reveal">
                    <div className="relative overflow-hidden rounded-2xl bg-white ring-1 ring-srm-gold/40 p-8 lg:p-10 shadow-[0_6px_24px_rgba(15,40,86,0.07)]">
                        <span aria-hidden className="pointer-events-none absolute left-0 top-0 h-[3px] w-full bg-srm-gold" />
                        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr_auto] gap-6 lg:gap-8 items-center">
                            <span className="inline-flex h-[56px] w-[56px] shrink-0 items-center justify-center rounded-full border border-srm-gold/45 text-srm-gold">
                                <ClipboardCheck className="h-[24px] w-[24px]" strokeWidth={1.6} />
                            </span>
                            <div>
                                <div className={EYEBROW}>See the standard behind SRM reporting</div>
                                <span aria-hidden className="mt-4 block h-[2px] w-10 bg-srm-gold" />
                                <h2 className="mt-4 font-display font-bold text-srm-navy text-[1.35rem] sm:text-[1.55rem] lg:text-[1.7rem] tracking-[-0.014em] leading-[1.22]">
                                    Independent, evidence-based, committee-ready.
                                </h2>
                                <p className="mt-4 text-[14.5px] lg:text-[15.5px] text-srm-body leading-[1.8]">
                                    SRM reports are prepared specifically for each
                                    property. Speak with our team to understand the
                                    photographic evidence, risk priorities and
                                    professional guidance included in an SRM assessment.
                                </p>
                            </div>
                            <div className="lg:justify-self-end">
                                <Link
                                    to="/contact"
                                    data-testid="services-reporting-lead-cta"
                                    className="group inline-flex items-center justify-center gap-2 rounded-sm bg-srm-gold text-white text-[11.5px] font-semibold tracking-[0.2em] uppercase px-8 h-[48px] border border-srm-gold hover:bg-srm-goldDark hover:border-srm-goldDark transition-all duration-300 whitespace-nowrap"
                                >
                                    Discuss SRM Reporting
                                    <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* === 2. Four-Stage Pathway ======================================= */
function FourStagePathway() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            id="pathway"
            data-testid="services-pathway"
            className="bg-white border-t border-srm-line scroll-mt-24"
        >
            <div className="container-srm py-12 lg:py-16">
                <div className="max-w-3xl mx-auto text-center reveal">
                    <div className={EYEBROW}>The Four-Stage Pathway</div>
                    <span aria-hidden className={`mx-auto mt-4 ${GOLD_RULE}`} />
                    <h2 className="mt-5 font-display font-bold text-[1.85rem] sm:text-[2.2rem] lg:text-[2.4rem] tracking-[-0.022em] leading-[1.12] text-srm-navy text-balance">
                        Engage one stage or combine several.
                    </h2>
                    <p className="mt-5 text-[15px] lg:text-[16.5px] text-srm-body leading-[1.75]">
                        SRM services are separately scoped. Clients may appoint SRM
                        for a single stage, selected stages or a continuous
                        relationship across the complete Guardian Program.
                    </p>
                </div>

                <div className="mt-10 lg:mt-14 grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
                    {STAGES.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <article
                                key={s.title}
                                data-testid={`service-stage-${i}`}
                                className="reveal group relative flex h-full flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-srm-line p-7 lg:p-8 shadow-[0_6px_24px_rgba(15,40,86,0.07)] transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-[0_28px_60px_rgba(15,40,86,0.14)]"
                                style={{ transitionDelay: `${(i % 2) * 80}ms` }}
                            >
                                <span
                                    aria-hidden
                                    className="pointer-events-none absolute left-0 top-0 h-[3px] w-16 bg-srm-gold transition-all duration-500 group-hover:w-full"
                                />
                                <div className="flex items-start gap-5">
                                    <span className="inline-flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-full border border-srm-gold/45 text-srm-gold transition-all duration-500 group-hover:bg-srm-gold/10">
                                        <Icon className="h-[26px] w-[26px]" strokeWidth={1.5} />
                                    </span>
                                    <div>
                                        <div className="font-display font-bold tracking-[0.2em] uppercase text-srm-navy/50 text-[10.5px]">
                                            Stage {s.step}
                                        </div>
                                        <h3 className="mt-1 font-display font-bold text-srm-navy text-[1.2rem] lg:text-[1.3rem] tracking-[-0.008em] leading-[1.25]">
                                            {s.title}
                                        </h3>
                                    </div>
                                </div>
                                <p className="mt-5 text-[14px] lg:text-[14.5px] text-srm-body leading-[1.75]">
                                    {s.body}
                                </p>
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}


/* === 5. Report Preview — split section ============================ */
function ReportPreview() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="services-report"
            className="bg-white border-t border-srm-line"
        >
            <div className="container-srm py-12 lg:py-16">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    {/* Left — copy */}
                    <div className="reveal">
                        <div className={EYEBROW}>Report Preview</div>
                        <span aria-hidden className={`mt-4 ${GOLD_RULE}`} />
                        <h2 className="mt-5 font-display font-bold text-[1.85rem] sm:text-[2.2rem] lg:text-[2.4rem] tracking-[-0.022em] leading-[1.12] text-srm-navy text-balance">
                            What an SRM report contains.
                        </h2>
                        <p className="mt-5 text-[15px] lg:text-[16.5px] text-srm-body leading-[1.75] max-w-[480px]">
                            Every SRM report follows the same clear structure —
                            written for committee decision-making and suitable for
                            insurance and maintenance records.
                        </p>

                        <ul className="mt-8 space-y-3.5">
                            {REPORT_SECTIONS.map((item) => (
                                <li
                                    key={item}
                                    className="flex items-start gap-3.5 text-[14.5px] lg:text-[15px] text-srm-navy"
                                >
                                    <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-srm-gold/10 ring-1 ring-srm-gold/40 text-srm-gold">
                                        <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2} />
                                    </span>
                                    <span className="leading-[1.55]">{item}</span>
                                </li>
                            ))}
                        </ul>

                        {/* Footer — Standards-Based Reporting */}
                        <div
                            data-testid="report-preview-standards"
                            className="mt-10 pt-8 border-t border-srm-line"
                        >
                            <div className="font-display font-bold tracking-[0.22em] uppercase text-srm-navy/65 text-[10.5px] lg:text-[11px]">
                                Standards-Based Reporting
                            </div>
                            <span aria-hidden className="mt-4 block h-[2px] w-10 bg-srm-gold" />
                            <p className="mt-4 text-[13.5px] lg:text-[14px] text-srm-body leading-[1.75] max-w-[480px]">
                                Where applicable, assessment findings are referenced to
                                the relevant Australian Standards, National Construction
                                Code (NCC), manufacturer installation requirements or
                                recognised industry best practice to support consistent,
                                evidence-based reporting.
                            </p>
                        </div>
                    </div>

                    {/* Right — stylised report mockup */}
                    <div className="reveal">
                        <ReportMockup />
                    </div>
                </div>
            </div>
        </section>
    );
}

/* Real photograph of an Australian-style residential strata complex —
 * directly representative of the asset SRM reports describe. Replaces
 * an earlier abstract CSS mockup that read as a placeholder. */
function ReportMockup() {
    return (
        <figure className="relative mx-auto max-w-[460px] aspect-[3/4] rounded-2xl bg-white ring-1 ring-srm-line shadow-[0_28px_60px_rgba(15,40,86,0.14)] overflow-hidden">
            <img
                src="https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1200&q=80"
                alt="Contemporary Australian residential strata complex — representative of the assets SRM independently assesses."
                loading="lazy"
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover"
            />
            {/* Navy gradient overlay for legibility of the caption strip */}
            <div
                aria-hidden
                className="absolute inset-0"
                style={{
                    background:
                        "linear-gradient(180deg, rgba(15,40,86,0.55) 0%, rgba(15,40,86,0.05) 35%, rgba(15,40,86,0) 60%, rgba(15,40,86,0.65) 100%)",
                }}
            />
            {/* Top caption — eyebrow */}
            <div className="absolute top-0 inset-x-0 px-8 pt-8">
                <span aria-hidden className="block h-[3px] w-12 bg-srm-gold" />
                <div className="mt-4 font-display font-bold tracking-[0.24em] uppercase text-srm-gold text-[10.5px]">
                    Assessed Asset
                </div>
            </div>
            {/* Bottom caption — report ribbon */}
            <figcaption className="absolute bottom-0 inset-x-0 px-8 pb-8">
                <div className="font-display font-bold text-white text-[1.25rem] lg:text-[1.4rem] tracking-[-0.012em] leading-[1.18]">
                    Roof Condition Assessment
                </div>
                <div className="mt-3 font-display font-semibold tracking-[0.2em] uppercase text-white/75 text-[10px]">
                    Independent · Evidence-Based · Committee-Ready
                </div>
            </figcaption>
            {/* Gold corner accent — preserved from prior treatment */}
            <span
                aria-hidden
                className="absolute top-0 right-0 h-[60px] w-[3px] bg-srm-gold"
            />
        </figure>
    );
}

/* === 6. Property Types — one concise line ======================= */
function PropertyTypes() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="services-property-types"
            className="bg-white border-t border-srm-line"
        >
            <div className="container-srm py-12 lg:py-16">
                <div className="max-w-3xl mx-auto text-center reveal">
                    <div className={EYEBROW}>Who SRM Works With</div>
                    <span aria-hidden className={`mx-auto mt-4 ${GOLD_RULE}`} />
                    <p className="mt-7 text-[15px] lg:text-[16.5px] text-srm-body leading-[1.85]">
                        SRM works with strata communities, body corporates, mixed-use
                        developments and professionally managed properties across
                        Australia.
                    </p>
                </div>
            </div>
        </section>
    );
}

/* === 7. Final CTA — navy ========================================== */
function FinalCTA() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="services-cta"
            className="relative overflow-hidden"
            style={{ backgroundColor: "#163E7A" }}
        >
            <div
                aria-hidden
                className="pointer-events-none absolute -top-32 -right-32 h-[380px] w-[380px] rounded-full"
                style={{ background: "radial-gradient(circle, rgba(201,165,92,0.10) 0%, rgba(201,165,92,0) 70%)" }}
            />
            <div
                aria-hidden
                className="pointer-events-none absolute -bottom-40 -left-20 h-[380px] w-[380px] rounded-full"
                style={{ background: "radial-gradient(circle, rgba(201,165,92,0.07) 0%, rgba(201,165,92,0) 70%)" }}
            />
            <div className="container-srm relative py-14 lg:py-20">
                <div className="max-w-3xl mx-auto text-center reveal">
                    <span aria-hidden className={`mx-auto ${GOLD_RULE}`} />
                    <h2 className="mt-5 font-display font-bold text-[1.85rem] sm:text-[2.3rem] lg:text-[2.55rem] tracking-[-0.024em] leading-[1.1] text-white text-balance">
                        Ready for independent roof asset advice?
                    </h2>
                    <p className="mt-5 text-[15px] lg:text-[16.5px] text-white/80 leading-[1.8] max-w-2xl mx-auto">
                        Speak with the SRM team about an independent assessment,
                        committee-ready reporting or a long-term roof asset plan —
                        tailored to your building, committee and obligations.
                    </p>
                    <div className="mt-8 flex justify-center">
                        <Link
                            to="/contact"
                            data-testid="services-cta-primary"
                            className="group inline-flex items-center justify-center gap-2 rounded-sm bg-srm-gold text-white text-[12px] font-semibold tracking-[0.2em] uppercase px-10 h-[54px] border border-srm-gold shadow-[0_10px_30px_rgba(201,165,92,0.30)] hover:bg-srm-goldDark hover:border-srm-goldDark hover:-translate-y-0.5 hover:shadow-[0_14px_36px_rgba(201,165,92,0.40)] transition-all duration-300"
                        >
                            Speak with the Team
                            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* === Page composition ============================================ */
export default function ServicesOverview() {
    usePageMeta({
        title: "Strata Roof Assessments & Condition Reports | SRM",
        description:
            "Independent strata roof condition assessments, committee-ready roof reports, scope of works and tender support, delivery oversight and ongoing monitoring for body corporates and managed buildings.",
        path: "/services",
        breadcrumbs: [
            { name: "Home", url: "/" },
            { name: "Services", url: "/services" },
        ],
    });
    useFAQSchema("services", FAQ_SERVICES);

    return (
        <div data-testid="services-page" className="min-h-screen bg-white">
            <Navbar />
            <main id="main">
                <ServicesHero />
                <ReportingLead />
                <FourStagePathway />
                <ReportPreview />
                <PropertyTypes />
                <FinalCTA />
            </main>
            <Footer />
        </div>
    );
}
